import { useEffect, useMemo, useState } from 'react'
import { machineNameOf } from './machineName'
import type { AgentRow, ProjectMember } from '../transport/api'
import { ownerDisplayName } from '../chat/rosterGroups'
import { PixelPet } from '../identity/PixelPet'
import { agentColor } from '../identity/agentColor'

/**
 * #agentAutocomplete — typing `@` in the composer offers the agent list; picking one
 * inserts a ping tag so this window's agent knows to message that one.
 *
 * Tag format is the original's exactly (index.html ~1300):
 *
 *   <ping-agent:HASH:session@host>␠     when the agent is on a mounted remote
 *   <ping-agent:HASH:session>␠          when it is local — NO dangling '@'
 *
 * The HASH is the address (read as everything between `ping-agent:` and the next `:` or
 * `>`); the label after it is only human-readable for the agent LLM. The trailing space
 * is part of the format — it keeps the tag from running into the next word.
 */

/** A pickable row: an account roster row, or a ROOM row wearing its owner's name. */
export type MentionAgent = AgentRow & {
  ownerName?: string | null
  mine?: boolean
  /** A friend's agent shared with the reader (phase 2): picking it links it with the open agent. */
  shareId?: string
}

/**
 * The @ list for a ROOM: its attached agents as mention rows (owner, 2026-08-27:
 * 朋友也可以用 @ ping owner 的 agent). Mine are enriched from my own roster by hash
 * — host and read_guide come from the machine I can actually ask; a friend's agent
 * carries its OWNER's name as the sub-label instead of a machine name (the busy
 * float's rule, applied to the picker).
 */
export function mentionRowsOfMembers(members: ProjectMember[], mine: AgentRow[]): MentionAgent[] {
  const mineByHash = new Map(mine.filter((a) => a.hash).map((a) => [a.hash!, a]))
  return members
    .filter((m) => m.kind === 'agent' && !!m.channel_hash)
    .map((m) => {
      const hash = m.channel_hash!
      const own = m.mine === true ? mineByHash.get(hash) : undefined
      if (own) return { ...own, mine: true }
      // The attach names agents "session @ host"; split so the tag and the name
      // column read exactly like a local row's.
      const raw = m.label ?? hash.slice(0, 8)
      const parts = raw.split(/\s+@\s+/)
      const session = (parts[0] ?? raw).trim()
      const host = parts.slice(1).join(' @ ').trim() || null
      return {
        hash,
        session,
        host,
        ownerName: ownerDisplayName(members, m),
        mine: m.mine === true,
      } as MentionAgent
    })
}

/**
 * The whole account's roster for a surface that lives inside a PANE.
 *
 * A pane's own roster query asks ONE machine — the one its conversation is on —
 * because it draws no rail and every other machine's answer was a round trip it
 * would not render (App.tsx, `paneScope`). But two things in a pane DO render the
 * whole account: the `@` picker and the note sheet's attach list. Owner, 2026-09-10:
 * 寬螢幕雙開 Agent 的時候 @ 選單只會出現部分的 Agent. Both read the pane's
 * single-machine list, so every agent on any other machine was simply absent —
 * not greyed, not "loading", absent.
 *
 * The fleet answer arrives later than the pane's own machine's; this merges them so
 * the local rows show at once and the rest append when the sweep lands. Fleet order
 * wins (it is what the shell shows); a row known only to the scoped list is kept,
 * never dropped, because the machine that answered fastest is also the one the
 * person is looking at.
 */
export function mergeRoster(fleet: AgentRow[] | undefined, scoped: AgentRow[]): AgentRow[] {
  if (!fleet || fleet.length === 0) return scoped
  const key = (a: AgentRow) => a.hash || `${a.session}\0${a.host ?? ''}`
  const seen = new Set(fleet.map(key))
  return [...fleet, ...scoped.filter((a) => !seen.has(key(a)))]
}

export function pingTag(a: AgentRow): string {
  const label = a.host ? `${a.session}@${a.host}` : a.session
  return `<ping-agent:${a.hash}${label ? `:${label}` : ''}> `
}

/**
 * Find an in-progress `@mention` immediately before the caret.
 *
 * Requires the `@` to start a word (start of text or after whitespace) so an email
 * address in the middle of a sentence doesn't open the picker.
 */
export function findMention(text: string, caret: number): { start: number; query: string } | null {
  const upto = text.slice(0, caret)
  const m = /(^|\s)@([\w.\-@]*)$/.exec(upto)
  if (!m) return null
  const query = m[2] ?? ''
  return { start: caret - query.length - 1, query }
}

export function AgentMention({
  agents,
  query,
  onPick,
  onDismiss,
}: {
  agents: MentionAgent[]
  query: string
  onPick: (a: MentionAgent) => void
  onDismiss: () => void
}) {
  const matches = useMemo(() => {
    const q = query.toLowerCase()
    // No cap. This used to slice(0, 8), which silently hid every further agent —
    // nothing said "more below", so the missing ones read as nonexistent. The list
    // scrolls; browsing the tail is the scroll's job, not a truncation's.
    return agents.filter(
      (a) =>
        a.session.toLowerCase().includes(q) ||
        (a.host ?? '').toLowerCase().includes(q) ||
        (a.ownerName ?? '').toLowerCase().includes(q),
    )
  }, [agents, query])

  /*
   * ONE KEY PER ROW, never the bare hash. A hash is the session name's alone
   * (agentIdentity.agentHash), so `gmi-agent` on two nodes of a shared home is one hash
   * twice — and React cannot remove a child whose key another child shares: filtered-out
   * rows stayed on screen above the one typed for (owner, 2026-10-21: @ 無法正確的 filter
   * 出來我要的 agent list). The machine tells them apart; a row with no machine that
   * still repeats a key (a room's members) takes its place in the list as well.
   */
  const keys = useMemo(() => {
    const seen = new Map<string, number>()
    return matches.map((a) => {
      const base = a.shareId ? `share:${a.shareId}` : `${a.device_id ?? ''}:${a.hash}`
      const n = seen.get(base) ?? 0
      seen.set(base, n + 1)
      return n ? `${base}#${n}` : base
    })
  }, [matches])

  const [active, setActive] = useState(0)

  // Reset the highlight whenever the candidate set changes, or it can point past the end.
  useEffect(() => {
    setActive(0)
  }, [query, agents])

  // Keydown is captured at the document so it wins over the composer's own handling
  // while the picker is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (matches.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActive((i) => (i + 1) % matches.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActive((i) => (i - 1 + matches.length) % matches.length)
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        // Enter is a newline everywhere else, but with an open picker selecting is what
        // you mean. Esc gets you back to a plain newline.
        e.preventDefault()
        const pick = matches[active]
        if (pick) onPick(pick)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onDismiss()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [matches, active, onPick, onDismiss])

  if (matches.length === 0) return null

  return (
    <div id="agentAutocomplete" className="open" role="listbox" aria-label="agents">
      {matches.map((a, i) => (
        <button
          key={keys[i]}
          type="button"
          role="option"
          aria-selected={i === active}
          className={`ac-row${i === active ? ' on' : ''}`}
          // Never steal focus from the composer.
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setActive(i)}
          onClick={() => onPick(a)}
        >
          <PixelPet name={a.session} seed={a.hash} size={22} />
          <span className="ac-name" style={{ color: agentColor(a.hash) }}>
            {a.session}
          </span>
          {/* WHOSE agent, not which machine, when it is not mine (owner, 2026-08-27:
              把所在機器的名稱換成 Agent Owner 的名稱) — a friend reading a machine
              name learns nothing about whose agent they are pinging. */}
          <span className="ac-host">{a.ownerName ?? machineNameOf(a)}</span>
          {/*
              WAS `!a.onboarded` — "this edge's create flow made this session", a
              lineage fact that is permanently false for every hand-made or pre-flow
              agent and never becomes true. The owner read it as an onboarding
              regression on two agents that had worked for days (為何你自己與 app
              builder 變成了 not onboarded). What the picker can usefully warn about
              is the thing its own gate acts on: the guide. Explicit === false, same
              as the gate — an older edge omits the field, and absent is not "unread".
            */}
            {a.read_guide === false && <span className="ac-new">guide unread</span>}
        </button>
      ))}
    </div>
  )
}
